import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

const MEAL_LABELS = { breakfast: "Desayuno", lunch: "Almuerzo", snack: "Merienda", dinner: "Cena", other: "Otra" };

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function normalizeLocalDateTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function factType(fact) {
  if (fact?.factKind === "time" || fact?.type === "actual") return "time";
  if (fact?.factKind === "meal" || fact?.source?.structuredData?.meal?.mealLogId) return "meal";
  return "life";
}

function initialForm(fact) {
  const type = factType(fact);
  const source = fact?.source || {};
  if (type === "time") {
    return {
      note: source.note || "",
      occurredAt: normalizeLocalDateTime(source.occurredAt),
      minutes: String(source.minutes || fact?.minutes || 1),
    };
  }
  if (type === "meal") {
    const meal = source?.structuredData?.meal || {};
    return {
      mealType: meal.mealType || "other",
      status: meal.status === "skipped" ? "skipped" : "eaten",
      description: meal.description || "",
      occurredAt: normalizeLocalDateTime(source.occurredAt),
    };
  }
  return {
    text: source.text || fact?.title || "",
    occurredAt: normalizeLocalDateTime(source.occurredAt || source.createdAt),
  };
}

export default function OwnerDayFactDialog({ fact, open, onClose, onChanged }) {
  const [form, setForm] = React.useState(() => initialForm(fact));
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const type = factType(fact);
  const source = fact?.source || {};
  const mealLogId = String(source?.structuredData?.meal?.mealLogId || "");

  React.useEffect(() => {
    if (!open || !fact) return;
    setForm(initialForm(fact));
    setBusy("");
    setError("");
    setConfirmingDelete(false);
  }, [fact, open]);

  const close = () => {
    if (busy) return;
    onClose?.();
  };

  async function save() {
    setError("");
    setConfirmingDelete(false);
    setBusy("save");
    try {
      if (type === "life") {
        const text = String(form.text || "").trim();
        if (!text) throw new Error("El registro no puede quedar vacío.");
        const occurredAt = new Date(form.occurredAt);
        if (Number.isNaN(occurredAt.getTime())) throw new Error("La fecha y hora no son válidas.");
        await ownerApi.updateLifeEntry(idOf(source), { text, occurredAt: occurredAt.toISOString() });
      } else if (type === "meal") {
        if (!mealLogId) throw new Error("No pude identificar el registro alimentario vinculado.");
        const occurredAt = new Date(form.occurredAt);
        if (Number.isNaN(occurredAt.getTime())) throw new Error("La fecha y hora no son válidas.");
        if (form.status === "eaten" && !String(form.description || "").trim()) throw new Error("Describí qué comiste antes de guardar.");
        await ownerApi.updateMeal(mealLogId, {
          mealType: form.mealType,
          status: form.status,
          description: String(form.description || "").trim(),
          occurredAt: occurredAt.toISOString(),
        });
      } else {
        const occurredAt = new Date(form.occurredAt);
        const minutes = Number(form.minutes);
        if (Number.isNaN(occurredAt.getTime())) throw new Error("La fecha y hora no son válidas.");
        if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) throw new Error("La duración debe estar entre 1 minuto y 24 horas.");
        await ownerApi.updateTimeEntry(idOf(source), {
          occurredAt: occurredAt.toISOString(),
          minutes: Math.round(minutes),
          note: String(form.note || "").trim(),
        });
      }
      await onChanged?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, err?.message || "No pude guardar el cambio."));
    } finally {
      setBusy("");
    }
  }

  async function remove() {
    setError("");
    setBusy("delete");
    try {
      if (type === "meal") {
        if (!mealLogId) throw new Error("No pude identificar el registro alimentario vinculado.");
        await ownerApi.deleteMeal(mealLogId);
      } else if (type === "time") {
        await ownerApi.deleteTimeEntry(idOf(source));
      } else {
        await ownerApi.deleteLifeEntry(idOf(source));
      }
      await onChanged?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar este registro."));
      setConfirmingDelete(false);
    } finally {
      setBusy("");
    }
  }

  const deleteText = type === "meal"
    ? "Eliminar también quita esta comida del registro de alimentación del Owner. No es sólo ocultarla de Hoy."
    : type === "time"
      ? "Eliminar quita este bloque de tiempo real y recalcula los minutos reales del día."
      : "Eliminar quita este hecho de forma persistente de Hoy.";

  return (
    <Dialog open={Boolean(open && fact)} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>Revisar registro de Hoy</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "text.secondary", fontWeight: 720, fontSize: 12.5, mb: 1.2 }}>
          {type === "meal" ? "Corregís la comida real; Hoy se vuelve a sincronizar con ese mismo registro." : type === "time" ? "Corregís el bloque real sin tocar sus relaciones con tarea, proyecto o persona." : "Corregís el hecho original sin crear una entrada nueva."}
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
        {confirmingDelete ? <Alert severity="warning" sx={{ mb: 1.2 }}>{deleteText}</Alert> : null}
        <Stack spacing={1.15}>
          {type === "life" ? (
            <TextField fullWidth multiline minRows={3} label="Qué pasó" value={form.text || ""} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, text: event.target.value }))} />
          ) : null}
          {type === "meal" ? (
            <>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField select fullWidth label="Comida" value={form.mealType || "other"} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, mealType: event.target.value }))}>
                  {Object.entries(MEAL_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField select fullWidth label="Estado" value={form.status || "eaten"} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                  <MenuItem value="eaten">Comí</MenuItem>
                  <MenuItem value="skipped">No comí / salteada</MenuItem>
                </TextField>
              </Stack>
              <TextField fullWidth multiline minRows={3} label="Qué comiste" value={form.description || ""} disabled={Boolean(busy) || form.status === "skipped"} helperText={form.status === "skipped" ? "Una comida salteada no guarda descripción ni estimaciones de alimentos." : ""} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
            </>
          ) : null}
          {type === "time" ? (
            <>
              {source?.commitment?.title ? <Alert icon={false} severity="info">Relacionado con: {source.commitment.title}</Alert> : null}
              <TextField fullWidth label="Nota / actividad" value={form.note || ""} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} />
              <TextField fullWidth type="number" label="Duración real (minutos)" inputProps={{ min: 1, max: 1440, step: 1 }} value={form.minutes || ""} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, minutes: event.target.value }))} />
            </>
          ) : null}
          <TextField fullWidth type="datetime-local" label="Fecha y hora real" InputLabelProps={{ shrink: true }} value={form.occurredAt || ""} disabled={Boolean(busy)} onChange={(event) => setForm((current) => ({ ...current, occurredAt: event.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2, flexWrap: "wrap", gap: .7 }}>
        {confirmingDelete ? (
          <Stack direction="row" spacing={.7}>
            <Button onClick={() => setConfirmingDelete(false)} disabled={Boolean(busy)}>No eliminar</Button>
            <Button color="error" variant="contained" onClick={remove} disabled={Boolean(busy)}>{busy === "delete" ? "Eliminando…" : "Sí, eliminar"}</Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={.7}>
            <Button color="error" onClick={() => setConfirmingDelete(true)} disabled={Boolean(busy)}>Eliminar</Button>
            <Button onClick={close} disabled={Boolean(busy)}>Cancelar</Button>
            <Button variant="contained" onClick={save} disabled={Boolean(busy)}>{busy === "save" ? "Guardando…" : "Guardar cambios"}</Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
